import React, { useState } from 'react';
import Button from './Button';
import TextInput from './TextInput';
import TextArea from './TextArea';

interface ContactForm {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface HelpContactPageProps {
  currentUser: any;
  onBack: () => void;
}

const HelpContactPage: React.FC<HelpContactPageProps> = ({ currentUser, onBack }) => {
  const [form, setForm] = useState<ContactForm>({
    name: '',
    email: currentUser?.email || '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      setError('Please fill in all fields.');
      return;
    }

    if (!form.email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Create mailto link with form data
      const subject = encodeURIComponent(`Support Request: ${form.subject}`);
      const body = encodeURIComponent(
        `Name: ${form.name}\nEmail: ${form.email}\n\nMessage:\n${form.message}\n\n---\nSent from AI Marker Mate Contact Form`
      );
      const mailtoLink = `mailto:nick.hull.42@gmail.com?subject=${subject}&body=${body}`;
      
      // Open mailto link
      window.location.href = mailtoLink;
      
      // Reset form and show success message
      setForm({
        name: '',
        email: currentUser?.email || '',
        subject: '',
        message: ''
      });
      
      setSuccess('Your message has been prepared in your default email client. Please send the email to complete your request.');
      
      // Clear success message after 10 seconds
      setTimeout(() => setSuccess(null), 10000);
    } catch (err) {
      setError('Failed to prepare email. Please try again or contact us directly at nick.hull.42@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof ContactForm, value: string) => {
    setForm({ ...form, [field]: value });
    // Clear errors when user starts typing
    if (error) setError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center text-primary-600 hover:text-primary-700 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-midnight">Help & Support</h1>
          <p className="text-gray-600 mt-2">Get assistance or provide feedback to help us improve</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-green-700 font-medium">{success}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-red-700 font-medium">{error}</p>
            </div>
          </div>
        )}

        <div className="space-y-8">
          {/* Contact Form */}
          <div className="bg-white rounded-xl shadow-soft p-8">
            <div className="flex items-center mb-6">
              <svg className="w-8 h-8 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <h2 className="text-2xl font-semibold text-midnight">Contact Us</h2>
            </div>
            
            <p className="text-gray-600 mb-8">
              We'd love to hear from you! Whether you have questions, feedback, or need technical support, 
              our team is here to help. Please fill out the form below and we'll get back to you as soon as possible.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <TextInput
                  label="Your Name"
                  id="name"
                  value={form.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('name', e.target.value)}
                  placeholder="Enter your full name"
                  required
                />
                
                <TextInput
                  label="Email Address"
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
                  placeholder="Enter your email address"
                  required
                />
              </div>

              <TextInput
                label="Subject"
                id="subject"
                value={form.subject}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('subject', e.target.value)}
                placeholder="What can we help you with?"
                required
              />

              <TextArea
                label="Message"
                id="message"
                value={form.message}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('message', e.target.value)}
                placeholder="Please describe your question, feedback, or issue in detail..."
                rows={6}
                required
              />

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  loadingText="Preparing..."
                  variant="primary"
                  size="lg"
                >
                  Send Message
                </Button>
                <Button
                  type="button"
                  onClick={onBack}
                  variant="secondary"
                  size="lg"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-xl shadow-soft p-8">
            <h2 className="text-2xl font-semibold text-midnight mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-6 last:border-b-0">
                <h3 className="text-lg font-semibold text-midnight mb-2">How do I get started with AI Marker Mate?</h3>
                <p className="text-gray-600">
                  Simply create an assessment, upload your student essays (as images, documents, or text), 
                  and let our AI provide detailed marking and feedback based on your rubric and criteria.
                </p>
              </div>

              <div className="border-b border-gray-200 pb-6 last:border-b-0">
                <h3 className="text-lg font-semibold text-midnight mb-2">How accurate is the AI marking?</h3>
                <p className="text-gray-600">
                  Our AI is specifically trained for Australian history curriculum and provides consistent, 
                  detailed feedback. However, it's designed to support and enhance teacher marking, not replace 
                  professional educator judgment.
                </p>
              </div>

              <div className="border-b border-gray-200 pb-6 last:border-b-0">
                <h3 className="text-lg font-semibold text-midnight mb-2">Can I customize the marking criteria?</h3>
                <p className="text-gray-600">
                  Yes! You can create custom assessments with your own rubrics, marking criteria, and system 
                  instructions to ensure the AI marking aligns with your specific requirements.
                </p>
              </div>

              <div className="border-b border-gray-200 pb-6 last:border-b-0">
                <h3 className="text-lg font-semibold text-midnight mb-2">Is my student data secure?</h3>
                <p className="text-gray-600">
                  Absolutely. We take data privacy seriously and comply with Australian privacy laws. 
                  Student essays are processed securely and we recommend using student numbers rather 
                  than full names for additional privacy.
                </p>
              </div>
            </div>
          </div>

          {/* Direct Contact */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-blue-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Need Immediate Help?</h3>
                <p className="text-blue-700 mb-2">
                  You can also reach us directly at: 
                  <a href="mailto:nick.hull.42@gmail.com" className="font-semibold hover:underline ml-1">
                    nick.hull.42@gmail.com
                  </a>
                </p>
                <p className="text-blue-600 text-sm">
                  We aim to respond to all inquiries within 24 hours during business days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpContactPage;
